import { FC, useMemo } from 'react';
import humanizeDuration from 'humanize-duration';

import { Link, Tooltip } from '@dbt-labs/sourdough';

import { ModelExecutionInfoNode } from '@/__generated__/graphql';
import { NodeStatusIconBadge } from '@/components/NodeStatusIconBadge';
import { PropertyCard } from '@/components/PropertyCard';
import { DetailsSection } from '@/components/SectionWithCard';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';

export type ExecutionInfo = Partial<Omit<ModelExecutionInfoNode, '__typename'>>;

interface RecentSectionProps {
  executionInfo: ExecutionInfo | null | undefined;
}

export const RecentSection: FC<RecentSectionProps> = ({ executionInfo }) => {
  const { projectId } = useDbtCloudProject();
  const { executedAt, executedAtUtc } = useMemo(() => {
    const date =
      executionInfo?.executeCompletedAt && new Date(executionInfo.executeCompletedAt);
    const executedAt = date && formatAbsoluteLocalDate(date);
    const executedAtUtc = date && formatDateCommon(date).utc;
    return {
      executedAt,
      executedAtUtc,
    };
  }, [executionInfo?.executeCompletedAt]);
  // TODO: I don't know what the units are! This currently assumes seconds
  const executionTime = useMemo(
    () =>
      executionInfo?.executionTime
        ? humanizeDuration(executionInfo.executionTime * 1000, {
            units: ['d', 'h', 'm', 's'],
            largest: 2,
            maxDecimalPoints: 2,
          })
        : null,
    [executionInfo?.executionTime],
  );
  const projectScope = useUrlProjectParams();
  const getRunLink = useLinkToDbtCloud('runDetails');
  const getJobLink = useLinkToDbtCloud('jobDetails');
  const runError = executionInfo?.lastRunError ?? undefined;
  if (!executionInfo) return null;
  return (
    <DetailsSection heading="Recent" withCard={false}>
      <div className="-m-1 flex w-full flex-wrap">
        <PropertyCard title={'Last run'}>
          <Tooltip content={executedAtUtc}>
            <div className="truncate">{executedAt}</div>
          </Tooltip>
        </PropertyCard>
        <PropertyCard title={'Execution time (excl. reuse)'}>
          {executionTime}
        </PropertyCard>
        {executionInfo.lastRunStatus && (
          <PropertyCard title={'Status'}>
            <NodeStatusIconBadge
              status={executionInfo.lastRunStatus}
              size="small"
              tooltip={runError}
            />
          </PropertyCard>
        )}
        <PropertyCard title={'Job ID'}>
          {projectId &&
            executionInfo.lastJobDefinitionId &&
            (getJobLink ? (
              <Link
                isInternal={false}
                to={getJobLink({
                  ...projectScope,
                  jobId: executionInfo.lastJobDefinitionId,
                  projectId,
                })}
              >
                {executionInfo.lastJobDefinitionId}
              </Link>
            ) : (
              executionInfo.lastJobDefinitionId
            ))}
          {!projectId && executionInfo.lastJobDefinitionId}
        </PropertyCard>
        <PropertyCard title={'Run ID'}>
          {projectId &&
            executionInfo.lastRunId &&
            (getRunLink ? (
              <Link
                isInternal={false}
                to={getRunLink({
                  ...projectScope,
                  runId: executionInfo.lastRunId,
                  projectId,
                })}
              >
                {executionInfo.lastRunId}
              </Link>
            ) : (
              executionInfo.lastRunId
            ))}
          {!projectId && executionInfo.lastRunId}
        </PropertyCard>
      </div>
    </DetailsSection>
  );
};
